import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { firestore } from '@config/firebase';
import { Landlord } from '../types/landlord.types';
import { Property } from '../types/property.types';
import { PropertyInquiry } from '../types/inquiry.types';

export interface LandlordDashboardState {
  landlord: Landlord | null;
  properties: Property[];
  inquiries: PropertyInquiry[];
  loading: boolean;
  error: string | null;
}

interface InternalLoadState {
  landlord: boolean;
  properties: boolean;
  inquiries: boolean;
}

// Keep landlord dashboard reads isolated from tenant flows.
export const useLandlordDashboard = (landlordId?: string) => {
  const [state, setState] = useState<LandlordDashboardState>({
    landlord: null,
    properties: [],
    inquiries: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!landlordId) {
      setState((prev) => ({
        ...prev,
        landlord: null,
        properties: [],
        inquiries: [],
        loading: false,
      }));
      return;
    }

    const pending: InternalLoadState = {
      landlord: true,
      properties: true,
      inquiries: true,
    };

    const markLoaded = (key: keyof InternalLoadState) => {
      pending[key] = false;
      if (!pending.landlord && !pending.properties && !pending.inquiries) {
        setState((prev) => ({ ...prev, loading: false }));
      }
    };

    const unsubscribes: Unsubscribe[] = [];

    // Landlord profile stream
    const landlordRef = doc(firestore, 'landlords', landlordId);
    unsubscribes.push(
      onSnapshot(
        landlordRef,
        (snapshot) => {
          const landlordData = snapshot.exists()
            ? ({ id: snapshot.id, ...snapshot.data() } as Landlord)
            : null;

          setState((prev) => ({
            ...prev,
            landlord: landlordData,
            error: null,
          }));
          markLoaded('landlord');
        },
        (err) => {
          console.error('Error loading landlord profile', err);
          setState((prev) => ({
            ...prev,
            error: err.code ?? 'FAILED',
            landlord: null,
          }));
          markLoaded('landlord');
        }
      )
    );

    // Properties stream scoped to landlord
    const propertiesQuery = query(
      collection(firestore, 'properties'),
      where('landlordId', '==', landlordId)
    );

    unsubscribes.push(
      onSnapshot(
        propertiesQuery,
        (snapshot) => {
          const properties = snapshot.docs
            .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
            .map((property) => ({
              ...property,
              // Defensive defaults for missing optional fields
              views: (property as any).views ?? 0,
              inquiries: (property as any).inquiries ?? 0,
            })) as Property[];

          setState((prev) => ({
            ...prev,
            properties,
            error: null,
          }));
          markLoaded('properties');
        },
        (err) => {
          console.error('Error loading properties', err);
          setState((prev) => ({
            ...prev,
            error: err.code ?? 'FAILED',
            properties: [],
          }));
          markLoaded('properties');
        }
      )
    );

    // Recent inquiries stream scoped to landlord
    const inquiriesQuery = query(
      collection(firestore, 'inquiries'),
      where('landlordId', '==', landlordId)
    );

    unsubscribes.push(
      onSnapshot(
        inquiriesQuery,
        (snapshot) => {
          const inquiries = snapshot.docs
            .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
            .map((inquiry) => ({
              ...inquiry,
              createdAt: normalizeTimestamp((inquiry as any).createdAt),
              updatedAt: normalizeTimestamp((inquiry as any).updatedAt),
            })) as PropertyInquiry[];

          setState((prev) => ({
            ...prev,
            inquiries,
            error: null,
          }));
          markLoaded('inquiries');
        },
        (err) => {
          console.error('Error loading inquiries', err);
          setState((prev) => ({
            ...prev,
            error: err.code ?? 'FAILED',
            inquiries: [],
          }));
          markLoaded('inquiries');
        }
      )
    );

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [landlordId]);

  return state;
};

const normalizeTimestamp = (value: Timestamp | Date | undefined) => {
  if (!value) return undefined;
  if (value instanceof Timestamp) return value;
  if (value instanceof Date) return Timestamp.fromDate(value);
  return undefined;
};
